import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';

const PREFIX = 'perfect-slice.endless-queue.v1:';
const SAVED = 'perfect-slice.endless-saved.v1:';
const EVENT = 'cubitplay-endless-queue';
const notify = () => window.dispatchEvent(new Event(EVENT));
function entries() {
  const values = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key?.startsWith(PREFIX)) {
        try {
          const entry = JSON.parse(localStorage.getItem(key));
          if (typeof entry?.round?.id === 'string' && key === PREFIX + entry.round.id &&
              Array.isArray(entry.round.cuts) && typeof entry.round.played_at === 'string' &&
              Number.isFinite(Date.parse(entry.round.played_at))) values.push({ ...entry, key });
        } catch { /* Ignore an invalid device record, not other saved plays. */ }
      }
    }
  } catch { /* Caller shows a device-storage warning on enqueue. */ }
  return values.sort((a, b) => a.round.played_at.localeCompare(b.round.played_at));
}
export function clearEndlessDeviceAccount(userId) {
  try {
    const remove = [];
    for (let i = 0; i < localStorage.length; i += 1) { const key = localStorage.key(i); if (key?.startsWith(SAVED + userId + ':')) remove.push(key); }
    entries().forEach(entry => { if (entry.owner_id === userId) remove.push(entry.key); });
    remove.forEach(key => localStorage.removeItem(key)); notify();
  } catch { /* Cloud deletion is authoritative. */ }
}

export function useEndlessSync(user, scope, onSaved) {
  const [revision, setRevision] = useState(0), [saving, setSaving] = useState(false), [error, setError] = useState('');
  const owner = user?.alias_confirmed ? user.id : null;
  const userRef = useRef(owner); userRef.current = owner;
  const running = useRef(false), scopeRef = useRef(scope); scopeRef.current = scope;
  const changedRef = useRef(onSaved); changedRef.current = onSaved;
  const lifetime = useRef(0);
  const identityKey = `${owner}:${scope}`;
  const identityRef = useRef(identityKey);
  if (identityRef.current !== identityKey) { identityRef.current = identityKey; lifetime.current += 1; }
  useEffect(() => () => { lifetime.current += 1; }, []);
  const sync = useCallback(async () => {
    const uid = userRef.current, currentScope = scopeRef.current, generation = lifetime.current;
    if (!uid || !currentScope || running.current || !navigator.onLine) return;
    const isCurrent = () => generation === lifetime.current;
    // Include the lock wait in the running lifetime; repeated events must not
    // queue another save using a captured, possibly obsolete account/scope.
    running.current = true;
    setSaving(true); setError('');
    let count = 0;
    const run = async () => {
      while (isCurrent() && navigator.onLine) {
        const batch = entries().filter(entry => entry.scope === currentScope && (!entry.owner_id || entry.owner_id === uid)).slice(0, 10);
        if (!batch.length) break;
        // Freeze ownership before network I/O. Never rewrite another account's queue.
        batch.forEach(entry => localStorage.setItem(entry.key, JSON.stringify({ round: entry.round, scope: entry.scope, owner_id: uid })));
        const result = await api('/endless/rounds', { method: 'POST', body: { rounds: batch.map(entry => entry.round) }, isCurrent });
        if (!isCurrent()) return;
        const accepted = new Set(result.saved);
        if (!batch.some(entry => accepted.has(entry.round.id))) throw new Error('Your plays are still on this device. Please try saving again.');
        batch.forEach(entry => {
          if (!accepted.has(entry.round.id)) return;
          localStorage.setItem(SAVED + uid + ':' + entry.round.id, '1');
          localStorage.removeItem(entry.key); count += 1;
        });
        notify();
      }
      if (count && isCurrent()) changedRef.current?.();
    };
    try {
      if (navigator.locks?.request) await navigator.locks.request('cubitplay-endless-save', run);
      else await run();
    } catch (err) {
      if (isCurrent()) setError(err.message || 'Your plays are safe on this device. Try saving again when you’re connected.');
    } finally {
      running.current = false; setSaving(false);
      // An account transition may have requested sync while this run held the lock.
      if (!isCurrent()) setRevision(value => value + 1);
    }
  }, []);
  useEffect(() => {
    const update = () => setRevision(value => value + 1);
    const online = () => { update(); sync(); };
    window.addEventListener(EVENT, update); window.addEventListener('storage', update); window.addEventListener('online', online);
    return () => { window.removeEventListener(EVENT, update); window.removeEventListener('storage', update); window.removeEventListener('online', online); };
  }, [sync]);
  useEffect(() => { if (owner && scope) sync(); else setError(''); }, [owner, scope, revision, sync]);
  const enqueue = useCallback(round => {
    const currentScope = scopeRef.current || (window.location.hostname.includes('.preview.') || ['localhost','127.0.0.1'].includes(window.location.hostname) ? 'preview' : 'production');
    try {
      const key = PREFIX + round.id;
      if (!localStorage.getItem(key) && !(userRef.current && localStorage.getItem(SAVED + userRef.current + ':' + round.id))) {
        localStorage.setItem(key, JSON.stringify({ round, scope: currentScope, owner_id: userRef.current }));
      }
      notify();
    } catch { setError('Device storage is full or blocked. Your local best is still shown, but this play could not be queued for your account.'); }
  }, []);
  const statusFor = useCallback(id => {
    if (!id) return 'none';
    try { if (owner && localStorage.getItem(SAVED + owner + ':' + id)) return 'saved'; } catch { /* Continue with helpful pending state. */ }
    if (error) return 'error';
    if (!owner) return 'guest';
    return saving ? 'saving' : 'pending';
  }, [owner, saving, error]);
  return { enqueue, sync, statusFor, saving, error, revision, pending: entries().filter(entry => entry.scope === scope && (!entry.owner_id || entry.owner_id === owner)).length };
}
